/**
 * UpstreamSnapshotsCard (iter84.60.5.1.5)
 *
 * Read-only observability of the `upstream_cache` history for a single
 * tournament. Renders the most recent snapshots newest first with
 * `is_current` / `is_pinned` badges, size, and pin metadata (who
 * pinned + when + note).
 *
 * Two reasons this exists NOW (not later):
 *   1. .60.5.1 landed the API-Football dual-write with no UI to prove
 *      it works from prod. This card is that proof surface — refresh
 *      an API-Football tournament, watch a new row appear at the top.
 *   2. Consolidates observability for BOTH ESPN and API-Football under
 *      one primitive. The next iter (.5.2+) adds fixtures projection;
 *      operators need to see the snapshot layer separately from
 *      fixtures to reason about rebuilds.
 *
 * iter86.11 — Pin / Unpin / Rollback actions added. Pinning marks a
 * known-good snapshot (also flips `is_current` + exempts it from
 * pruning); Rollback = pin + rebuild-from-cache (overrides preserved).
 *
 * Endpoints:
 *   GET  /api/site-admin/tournaments/{tid}/upstream-snapshots
 *   POST /api/site-admin/tournaments/{tid}/upstream-snapshots/{sid}/pin
 *   POST /api/site-admin/tournaments/{tid}/upstream-snapshots/{sid}/unpin
 *   POST /api/site-admin/tournaments/{tid}/upstream-snapshots/{sid}/rollback
 */
import { useCallback, useEffect, useState } from "react";
import { Database, RefreshCw, Loader2, Pin, PinOff, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Render a byte-count into "12.4 KB" / "3.2 MB". Snapshots on prod tend
// to be 10-100 KB for ESPN and 20-300 KB for API-Football — anything
// bigger is unusual and worth a glance.
function formatBytes(n) {
    if (n == null) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function formatAge(iso) {
    if (!iso) return "—";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return iso;
    const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

// iter86.11 — Per-row Pin / Unpin / Rollback actions with an inline
// note + confirm step (no modal — keeps the timeline scannable).
function RowActions({ tid, snap, onDone }) {
    const [mode, setMode] = useState(null); // null | "pin" | "rollback"
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const run = async (fn, successMsg) => {
        setBusy(true);
        try {
            const r = await fn();
            toast.success(typeof successMsg === "function" ? successMsg(r) : successMsg);
            setMode(null);
            setNote("");
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    if (mode) {
        const isPin = mode === "pin";
        return (
            <div className="flex items-center gap-1.5 flex-wrap">
                <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={isPin ? "Pin note (optional)" : "Rollback note (optional)"}
                    className="brutal-input text-[10px] py-0.5 px-1.5 w-36"
                    data-testid={`snapshot-${mode}-note-${snap.id}`}
                />
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => isPin
                        ? run(() => api.sitePinSnapshot(tid, snap.id, note),
                            "Pinned — rebuilds now use this snapshot.")
                        : run(() => api.siteRollbackToSnapshot(tid, snap.id, note),
                            (r) => `Rolled back — ${r.rebuild?.fixtures_after ?? "?"} fixture(s) rebuilt from this snapshot, overrides preserved.`)}
                    className={`brutal-btn text-[10px] py-0.5 px-2 ${isPin ? "bg-blaze/20 border-blaze text-blaze" : "bg-amber-500/20 border-amber-500 text-amber-300"}`}
                    data-testid={`snapshot-${mode}-confirm-${snap.id}`}
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : (isPin ? "Confirm pin" : "Confirm rollback")}
                </button>
                <button
                    type="button"
                    onClick={() => { setMode(null); setNote(""); }}
                    className="brutal-btn-ghost text-[10px] py-0.5 px-1.5"
                    data-testid={`snapshot-${mode}-cancel-${snap.id}`}
                >
                    Cancel
                </button>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-1.5">
            {snap.is_pinned ? (
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => run(() => api.siteUnpinSnapshot(tid, snap.id),
                        "Unpinned — stays current until the next fetch or pin.")}
                    className="brutal-btn-ghost text-[10px] py-0.5 px-1.5 flex items-center gap-1"
                    title="Remove the pin. The snapshot stays current until a fresh fetch lands."
                    data-testid={`snapshot-unpin-btn-${snap.id}`}
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <PinOff className="w-3 h-3" />} Unpin
                </button>
            ) : (
                <button
                    type="button"
                    onClick={() => setMode("pin")}
                    className="brutal-btn-ghost text-[10px] py-0.5 px-1.5 flex items-center gap-1"
                    title="Mark as known-good: rebuilds use this snapshot and pruning never deletes it."
                    data-testid={`snapshot-pin-btn-${snap.id}`}
                >
                    <Pin className="w-3 h-3" /> Pin
                </button>
            )}
            {!snap.is_current && (
                <button
                    type="button"
                    onClick={() => setMode("rollback")}
                    className="brutal-btn-ghost text-[10px] py-0.5 px-1.5 flex items-center gap-1 text-amber-300"
                    title="Pin this snapshot AND rebuild fixtures from it now (manual overrides preserved)."
                    data-testid={`snapshot-rollback-btn-${snap.id}`}
                >
                    <Undo2 className="w-3 h-3" /> Roll back
                </button>
            )}
        </div>
    );
}

export function UpstreamSnapshotsCard({ tournament }) {
    const tid = tournament?.id;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const load = useCallback(async () => {
        if (!tid) return;
        setLoading(true);
        try {
            const resp = await api.siteUpstreamSnapshots(tid, { limit: 20 });
            setData(resp);
        } catch (e) {
            toast.error(`Snapshot list failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
            setLoading(false);
        }
    }, [tid]);

    useEffect(() => {
        load();
    }, [load]);

    if (!tid) return null;

    const key = data?.key;
    const snapshots = data?.snapshots || [];
    const total = data?.count_total || 0;

    return (
        <div
            className="border border-zinc-800 rounded-lg p-4 space-y-3 bg-zinc-950/40"
            data-testid="upstream-snapshots-card"
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-300 flex items-center gap-2">
                        <Database className="w-4 h-4 text-neon" />
                        Upstream Cache History
                    </h3>
                    <p className="text-xs text-zinc-500 mt-1">
                        Last {snapshots.length} of {total} snapshot(s) for this tournament's
                        upstream feed, newest first. <b className="text-zinc-400">Pin</b> a known-good
                        snapshot to protect it and make rebuilds use it; <b className="text-zinc-400">Roll back</b> pins
                        + rebuilds fixtures from it immediately (overrides preserved).
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="brutal-btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5 shrink-0"
                    data-testid="upstream-snapshots-refresh-btn"
                >
                    {loading ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                        <RefreshCw className="w-3 h-3" />
                    )}
                    Refresh
                </button>
            </div>

            {key ? (
                <div
                    className="text-[11px] font-mono text-zinc-400 bg-zinc-900/60 rounded px-2 py-1.5 inline-block"
                    data-testid="upstream-snapshots-key"
                >
                    <span className="text-zinc-500">source</span>=<span className="text-neon">{key.source}</span>
                    {" · "}
                    <span className="text-zinc-500">sport</span>=<span className="text-neon">{key.sport}</span>
                    {" · "}
                    <span className="text-zinc-500">league</span>=<span className="text-neon">{key.league}</span>
                </div>
            ) : (tournament?.template || "").startsWith("golf_") ? (
                <p
                    className="text-xs text-zinc-500 italic"
                    data-testid="upstream-snapshots-no-key"
                >
                    Golf tournament — leaderboard data flows through the <b className="text-zinc-400">Slash Golf cache</b> (TTL-based:
                    ~15 min refresh, 5 min on the final day), not fixture snapshots. There are no match fixtures to pin or roll back here.
                </p>
            ) : (tournament?.template || "").includes("nfl_season") ? (
                <p
                    className="text-xs text-zinc-500 italic"
                    data-testid="upstream-snapshots-no-key"
                >
                    NFL Season — data flows through the <b className="text-zinc-400">API-Sports American Football cache</b> (teams 7d ·
                    games/standings 15 min · odds 6h TTL), not fixture snapshots. There are no fixtures to pin or roll back here.
                </p>
            ) : (
                <p
                    className="text-xs text-zinc-500 italic"
                    data-testid="upstream-snapshots-no-key"
                >
                    Manual tournament — no upstream feed wired. Nothing to show here.
                </p>
            )}

            {key && snapshots.length === 0 && !loading && (
                <p
                    className="text-xs text-zinc-500 italic"
                    data-testid="upstream-snapshots-empty"
                >
                    No snapshots yet. Trigger a fetch (e.g. Refresh scores) and reload.
                </p>
            )}

            {snapshots.length > 0 && (
                <div className="overflow-x-auto">
                    <table
                        className="w-full text-xs"
                        data-testid="upstream-snapshots-table"
                    >
                        <thead>
                            <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-wider">
                                <th className="p-2 w-24">When</th>
                                <th className="p-2 w-20 text-center">Current</th>
                                <th className="p-2 w-20 text-center">Pinned</th>
                                <th className="p-2 w-20 text-right">Size</th>
                                <th className="p-2">Note / Pin metadata</th>
                                <th className="p-2 w-44">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {snapshots.map((s) => (
                                <tr
                                    key={s.id}
                                    className="border-t border-zinc-900"
                                    data-testid={`upstream-snapshots-row-${s.id}`}
                                >
                                    <td className="p-2 text-zinc-300" title={s.snapshot_at || ""}>
                                        {formatAge(s.snapshot_at)}
                                    </td>
                                    <td className="p-2 text-center">
                                        {s.is_current ? (
                                            <span
                                                className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-neon/20 text-neon"
                                                data-testid={`upstream-snapshots-current-${s.id}`}
                                            >
                                                CURRENT
                                            </span>
                                        ) : (
                                            <span className="text-zinc-600">—</span>
                                        )}
                                    </td>
                                    <td className="p-2 text-center">
                                        {s.is_pinned ? (
                                            <span
                                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blaze/20 text-blaze"
                                                data-testid={`upstream-snapshots-pinned-${s.id}`}
                                            >
                                                <Pin className="w-2.5 h-2.5" />
                                                PINNED
                                            </span>
                                        ) : (
                                            <span className="text-zinc-600">—</span>
                                        )}
                                    </td>
                                    <td className="p-2 text-right text-zinc-400 font-mono">
                                        {formatBytes(s.size_bytes)}
                                    </td>
                                    <td className="p-2 text-zinc-500">
                                        {s.is_pinned && s.pinned_by ? (
                                            <span>
                                                by <span className="text-zinc-300">{s.pinned_by}</span>
                                                {" · "}{formatAge(s.pinned_at)}
                                                {s.note ? ` · "${s.note}"` : ""}
                                            </span>
                                        ) : s.note ? (
                                            <span>"{s.note}"</span>
                                        ) : (
                                            <span className="text-zinc-700">—</span>
                                        )}
                                    </td>
                                    <td className="p-2">
                                        <RowActions tid={tid} snap={s} onDone={load} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
